import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

// KB articles pilot (Storm 5 Nov 07:41, "go KB articles pilot"): knowledge is shown
// as a structured card, not one run of plain text. Headings, numbered steps (kept in
// order), bullets, bold, and "do not / never" lines flagged as warnings. Built from
// React elements only: no innerHTML, so article text can never inject markup.
// Long articles open collapsed with "Open full article".

const WARN = /^(do not|don't|never|internal reference)\b/i;
const SOURCE_TAG = /\s*\[source: [^\]]+\]\s*$/i;

function inline(text, key) {
  // **bold** only; everything else is literal text.
  const parts = String(text).replace(SOURCE_TAG, '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => (p.startsWith('**') && p.endsWith('**')
    ? <strong key={`${key}-${i}`}>{p.slice(2, -2)}</strong>
    : <React.Fragment key={`${key}-${i}`}>{p}</React.Fragment>));
}

// Text -> blocks. Wrapped lines (indented continuation) join the item above them.
export function parseArticle(text) {
  const blocks = [];
  let list = null;
  const close = () => { if (list) { blocks.push(list); list = null; } };
  for (const raw of String(text || '').split('\n')) {
    const line = raw.trimEnd();
    if (!line.trim()) { close(); continue; }
    const t = line.trim();
    let m;
    if ((m = t.match(/^(#{1,3})\s+(.*)$/))) { close(); blocks.push({ kind: m[1].length === 1 ? 'title' : 'heading', text: m[2] }); continue; }
    if ((m = t.match(/^(\d+)[.)]\s+(.*)$/))) {
      if (!list || list.kind !== 'steps') { close(); list = { kind: 'steps', items: [] }; }
      list.items.push(m[2]); continue;
    }
    if ((m = t.match(/^[-*•]\s+(.*)$/))) {
      if (!list || list.kind !== 'bullets') { close(); list = { kind: 'bullets', items: [] }; }
      list.items.push(m[1]); continue;
    }
    if (list && /^\s+/.test(raw)) { list.items[list.items.length - 1] += ` ${t}`; continue; }
    close();
    const prev = blocks[blocks.length - 1];
    if (prev && prev.kind === 'para' && !WARN.test(t)) { prev.text += ` ${t}`; continue; }
    blocks.push({ kind: WARN.test(t) ? 'warn' : 'para', text: t });
  }
  close();
  return blocks;
}

function Block({ b, k }) {
  if (b.kind === 'title') return <div className="kb-art-title">{inline(b.text, k)}</div>;
  if (b.kind === 'heading') return <div className="kb-art-heading">{inline(b.text, k)}</div>;
  if (b.kind === 'warn') return <div className="kb-art-warn"><AlertTriangle size={11} /><span>{inline(b.text, k)}</span></div>;
  if (b.kind === 'steps') return <ol className="kb-art-steps">{b.items.map((x, i) => <li key={i}>{inline(x, `${k}-${i}`)}</li>)}</ol>;
  if (b.kind === 'bullets') return <ul className="kb-art-bullets">{b.items.map((x, i) => (
    WARN.test(x) ? <li key={i} className="kb-art-warn-li">{inline(x, `${k}-${i}`)}</li> : <li key={i}>{inline(x, `${k}-${i}`)}</li>))}</ul>;
  return <p className="kb-art-para">{inline(b.text, k)}</p>;
}

const COLLAPSED_BLOCKS = 4;

export default function ArticleText({ text }) {
  const [open, setOpen] = useState(false);
  const blocks = parseArticle(text);
  const long = blocks.length > COLLAPSED_BLOCKS;
  const shown = open || !long ? blocks : blocks.slice(0, COLLAPSED_BLOCKS);
  return (
    <div className="kb-art">
      {shown.map((b, i) => <Block key={i} b={b} k={`b${i}`} />)}
      {long && (
        <button type="button" className="kb-art-more" onClick={() => setOpen(!open)}>
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {open ? 'Show less' : 'Open full article'}
        </button>
      )}
    </div>
  );
}
